/**
 * Outil « Tableau d'avancement » — logique pure (physique-chimie, 2nde → Tle).
 *
 * Une réaction a A + b B → c C + d D, des quantités initiales n₀ : au cours
 * de la transformation, n(réactif) = n₀ − ν x et n(produit) = n₀ + ν x. Le
 * réactif limitant est celui dont n₀/ν est le plus petit : x_max = min(n₀/ν).
 * Mélange stœchiométrique : tous les n₀/ν égaux. En Tle, une transformation
 * non totale s'arrête à x_f < x_max : taux d'avancement final τ = x_f / x_max.
 * Les espèces en excès (H⁺ d'un milieu acidifié) ou solvant (H₂O) n'entrent
 * pas dans la recherche du réactif limitant.
 */

import { formatNumber } from '../../ui/format';

export type State = 's' | 'l' | 'g' | 'aq';

export interface Species {
  /** « CH₄ » */
  text: string;
  /** « méthane » */
  name: string;
  state: State;
}

export interface Term {
  sp: Species;
  nu: number;
  /** espèce en excès ou solvant : pas de quantité suivie */
  role?: 'excès' | 'solvant';
}

export interface Reaction {
  id: string;
  label: string;
  reactants: Term[];
  products: Term[];
  unit: 'mol' | 'mmol';
  /** n₀ des réactifs suivis (dans l'unité), par défaut */
  init: number[];
  /** transformation typiquement non totale (Tle), τ par défaut */
  tau?: number;
  note?: string;
}

const s = (text: string, name: string, state: State): Species => ({ text, name, state });
const SP = {
  CH4: s('CH₄', 'méthane', 'g'),
  O2: s('O₂', 'dioxygène', 'g'),
  CO2: s('CO₂', 'dioxyde de carbone', 'g'),
  H2Og: s('H₂O', 'eau', 'g'),
  H2Ol: s('H₂O', 'eau', 'l'),
  C3H8: s('C₃H₈', 'propane', 'g'),
  C2H6O: s('C₂H₆O', 'éthanol', 'l'),
  Fe: s('Fe', 'fer', 's'),
  Fe2O3: s('Fe₂O₃', 'oxyde de fer III', 's'),
  Zn: s('Zn', 'zinc', 's'),
  Hp: s('H⁺', 'ion hydrogène', 'aq'),
  Zn2: s('Zn²⁺', 'ion zinc', 'aq'),
  H2: s('H₂', 'dihydrogène', 'g'),
  Cu2: s('Cu²⁺', 'ion cuivre II', 'aq'),
  Cu: s('Cu', 'cuivre', 's'),
  MnO4: s('MnO₄⁻', 'ion permanganate', 'aq'),
  Fe2: s('Fe²⁺', 'ion fer II', 'aq'),
  Mn2: s('Mn²⁺', 'ion manganèse II', 'aq'),
  Fe3: s('Fe³⁺', 'ion fer III', 'aq'),
  I2: s('I₂', 'diiode', 'aq'),
  S2O3: s('S₂O₃²⁻', 'ion thiosulfate', 'aq'),
  Im: s('I⁻', 'ion iodure', 'aq'),
  S4O6: s('S₄O₆²⁻', 'ion tétrathionate', 'aq'),
  Ag: s('Ag⁺', 'ion argent', 'aq'),
  Cl: s('Cl⁻', 'ion chlorure', 'aq'),
  AgCl: s('AgCl', 'chlorure d’argent', 's'),
  HO: s('HO⁻', 'ion hydroxyde', 'aq'),
  CuOH2: s('Cu(OH)₂', 'hydroxyde de cuivre II', 's'),
  N2: s('N₂', 'diazote', 'g'),
  NH3: s('NH₃', 'ammoniac', 'g'),
  Al: s('Al', 'aluminium', 's'),
  S: s('S', 'soufre', 's'),
  Al2S3: s('Al₂S₃', 'sulfure d’aluminium', 's'),
  AcOH: s('CH₃COOH', 'acide éthanoïque', 'aq'),
  AcO: s('CH₃COO⁻', 'ion éthanoate', 'aq'),
  H3O: s('H₃O⁺', 'ion oxonium', 'aq'),
  AcOHl: s('CH₃COOH', 'acide éthanoïque', 'l'),
  EtOH: s('C₂H₅OH', 'éthanol', 'l'),
  Ester: s('CH₃COOC₂H₅', 'éthanoate d’éthyle', 'l'),
} as const;

const t = (sp: Species, nu = 1, role?: Term['role']): Term =>
  role ? { sp, nu, role } : { sp, nu };

export const REACTIONS: readonly Reaction[] = [
  {
    id: 'methane',
    label: 'Combustion du méthane',
    reactants: [t(SP.CH4), t(SP.O2, 2)],
    products: [t(SP.CO2), t(SP.H2Og, 2)],
    unit: 'mol',
    init: [2, 3],
  },
  {
    id: 'propane',
    label: 'Combustion du propane',
    reactants: [t(SP.C3H8), t(SP.O2, 5)],
    products: [t(SP.CO2, 3), t(SP.H2Og, 4)],
    unit: 'mol',
    init: [1, 4],
  },
  {
    id: 'ethanol',
    label: 'Combustion de l’éthanol',
    reactants: [t(SP.C2H6O), t(SP.O2, 3)],
    products: [t(SP.CO2, 2), t(SP.H2Og, 3)],
    unit: 'mol',
    init: [0.5, 2],
  },
  {
    id: 'rust',
    label: 'Le fer rouille (oxydation par le dioxygène)',
    reactants: [t(SP.Fe, 4), t(SP.O2, 3)],
    products: [t(SP.Fe2O3, 2)],
    unit: 'mol',
    init: [2, 2],
  },
  {
    id: 'zinc-acid',
    label: 'Zinc dans l’acide chlorhydrique',
    reactants: [t(SP.Zn), t(SP.Hp, 2)],
    products: [t(SP.Zn2), t(SP.H2)],
    unit: 'mmol',
    init: [5, 6],
  },
  {
    id: 'copper-zinc',
    label: 'Ions cuivre II et zinc',
    reactants: [t(SP.Cu2), t(SP.Zn)],
    products: [t(SP.Cu), t(SP.Zn2)],
    unit: 'mmol',
    init: [2, 3],
  },
  {
    id: 'permanganate',
    label: 'Ions permanganate et fer II (milieu acide)',
    reactants: [t(SP.MnO4), t(SP.Fe2, 5), t(SP.Hp, 8, 'excès')],
    products: [t(SP.Mn2), t(SP.Fe3, 5), t(SP.H2Ol, 4, 'solvant')],
    unit: 'mmol',
    init: [1, 4],
    note: 'H⁺ est en excès (milieu acidifié) et H₂O est le solvant : ils ne limitent jamais.',
  },
  {
    id: 'iodine',
    label: 'Diiode et ions thiosulfate',
    reactants: [t(SP.I2), t(SP.S2O3, 2)],
    products: [t(SP.Im, 2), t(SP.S4O6)],
    unit: 'mmol',
    init: [1.5, 2],
  },
  {
    id: 'silver',
    label: 'Précipitation du chlorure d’argent',
    reactants: [t(SP.Ag), t(SP.Cl)],
    products: [t(SP.AgCl)],
    unit: 'mmol',
    init: [2, 1.5],
  },
  {
    id: 'copper-hydroxide',
    label: 'Précipitation de l’hydroxyde de cuivre II',
    reactants: [t(SP.Cu2), t(SP.HO, 2)],
    products: [t(SP.CuOH2)],
    unit: 'mmol',
    init: [1, 3],
  },
  {
    id: 'ammonia',
    label: 'Synthèse de l’ammoniac',
    reactants: [t(SP.N2), t(SP.H2, 3)],
    products: [t(SP.NH3, 2)],
    unit: 'mol',
    init: [1, 3],
  },
  {
    id: 'aluminium-sulfur',
    label: 'Aluminium et soufre',
    reactants: [t(SP.Al, 2), t(SP.S, 3)],
    products: [t(SP.Al2S3)],
    unit: 'mol',
    init: [1, 1.2],
  },
  {
    id: 'acetic',
    label: 'Acide éthanoïque dans l’eau (non totale)',
    reactants: [t(SP.AcOH), t(SP.H2Ol, 1, 'solvant')],
    products: [t(SP.AcO), t(SP.H3O)],
    unit: 'mmol',
    init: [1],
    tau: 0.04,
    note: 'L’eau est le solvant. La transformation est très limitée : pour 1 mmol d’acide dans 100 mL, x_f ≈ 0,04 x_max (τ ≈ 4 %).',
  },
  {
    id: 'ester',
    label: 'Estérification (non totale)',
    reactants: [t(SP.AcOHl), t(SP.EtOH)],
    products: [t(SP.Ester), t(SP.H2Ol)],
    unit: 'mol',
    init: [1, 1],
    tau: 0.67,
    note: 'Pour un mélange équimolaire d’acide et d’alcool, la transformation s’arrête vers x_f ≈ 0,67 x_max (τ ≈ 67 %) ; avec un excès d’alcool, elle va plus loin.',
  },
];

export const reactionById = (id: string) => REACTIONS.find((r) => r.id === id) ?? REACTIONS[0]!;

/** espèces dont on suit la quantité (ni excès ni solvant) */
export const tracked = (ts: readonly Term[]) => ts.filter((x) => !x.role);
export const allTerms = (r: Reaction) => [...r.reactants, ...r.products];

/* ---------- écriture ---------- */

const NB = String.fromCharCode(0xa0);
const STATE_TEXT: Record<State, string> = { s: '(s)', l: '(ℓ)', g: '(g)', aq: '(aq)' };

const termText = (x: Term, states: boolean) =>
  `${x.nu === 1 ? '' : `${x.nu}${NB}`}${x.sp.text}${states ? STATE_TEXT[x.sp.state] : ''}`;

/**
 * « CH₄(g) + 2 O₂(g) → CO₂(g) + 2 H₂O(g) » ; une transformation non totale
 * (Tle : acide éthanoïque, estérification) s'écrit avec « ⇄ ».
 */
export function equationText(r: Reaction, states = true): string {
  const side = (ts: readonly Term[]) => ts.map((x) => termText(x, states)).join(' + ');
  return `${side(r.reactants)} ${r.tau ? '⇄' : '→'} ${side(r.products)}`;
}

/** pour le lecteur d'écran : « méthane plus 2 dioxygène donnent … » */
export function equationSpoken(r: Reaction): string {
  // « 5 ions fer II » : un nom d'ion au pluriel après un coefficient
  const named = (x: Term) =>
    x.nu === 1 ? x.sp.name : `${x.nu} ${x.sp.name.replace(/^ion /, 'ions ')}`;
  const side = (ts: readonly Term[]) => ts.map(named).join(' plus ');
  return `${side(r.reactants)} donnent ${side(r.products)}`;
}

/** décimales gardées : 4 chiffres significatifs au plus (4 décimales au plus) */
const digitsOf = (v: number) =>
  Math.abs(v) < 1e-12 ? 0 : Math.max(0, Math.min(4, 3 - Math.floor(Math.log10(Math.abs(v)))));
/** valeur affichée par `fmt` */
const shown = (v: number) => Number(v.toFixed(digitsOf(v)));
/** l'écriture de `fmt` est exacte */
export const exactShown = (v: number) => Math.abs(shown(v) - v) < 1e-9 * Math.max(1, Math.abs(v));

/** nombre en français, 4 chiffres significatifs au plus */
export const fmt = (v: number): string => formatNumber(shown(v), digitsOf(v));

/** « = 1,5 » si l'écriture est exacte, « ≈ 0,333 » sinon */
export const eq = (v: number): string => `${exactShown(v) ? '=' : '≈'}${NB}${fmt(v)}`;

/** « 2 mol », « 1,5 mmol » */
export const qty = (v: number, unit: Reaction['unit']) => `${fmt(v)}${NB}${unit}`;

/** « 3 − 2x », « x », « 0,5 + x » (n₀ en unité de la réaction) */
export function exprText(n0: number, nu: number, reactant: boolean): string {
  const ax = nu === 1 ? 'x' : `${nu}x`;
  if (n0 === 0) return reactant ? `−${ax}` : ax;
  // insécable : « 3 − 2x » ne se coupe pas
  return `${fmt(n0)}${NB}${reactant ? '−' : '+'}${NB}${ax}`;
}

/* ---------- avancement ---------- */

export interface Limit {
  /** n₀/ν de chaque réactif suivi (même ordre que tracked(reactants)) */
  ratios: number[];
  xmax: number;
  /** indices (dans tracked(reactants)) des réactifs limitants */
  limiting: number[];
  /** tous les réactifs suivis sont limitants */
  stoichiometric: boolean;
}

const close = (a: number, b: number) =>
  Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

export function limitOf(r: Reaction, n0: readonly number[]): Limit {
  const rs = tracked(r.reactants);
  const ratios = rs.map((x, i) => (n0[i] ?? 0) / x.nu);
  const xmax = Math.min(...ratios);
  const limiting = ratios.flatMap((v, i) => (close(v, xmax) ? [i] : []));
  return { ratios, xmax, limiting, stoichiometric: rs.length > 1 && limiting.length === rs.length };
}

/** quantité de chaque espèce suivie à l'avancement x : réactifs suivis puis produits suivis */
export function amountsAt(r: Reaction, n0: readonly number[], x: number): number[] {
  const rs = tracked(r.reactants);
  const ps = tracked(r.products);
  return [
    ...rs.map((t2, i) => Math.max(0, (n0[i] ?? 0) - t2.nu * x)),
    ...ps.map((t2) => t2.nu * x),
  ];
}

/**
 * Phrases du raisonnement : une hypothèse par réactif, puis la conclusion.
 * `total` faux (transformation non totale) : x_max est ce qu'on atteindrait
 * si elle était totale (« serait entièrement consommé »).
 */
export function limitLines(r: Reaction, n0: readonly number[], total = true): string[] {
  const used = total
    ? 'est entièrement consommé'
    : 'serait entièrement consommé si la transformation était totale';
  const usedAll = total
    ? 'tous les réactifs sont entièrement consommés'
    : 'tous les réactifs seraient consommés si la transformation était totale';
  const rs = tracked(r.reactants);
  const L = limitOf(r, n0);
  const lines = rs.map((x, i) => {
    const e = exprText(n0[i] ?? 0, x.nu, true).replace('x', 'x_max');
    return `Si ${x.sp.text} est limitant : ${e} = 0, donc x_max ${eq(L.ratios[i]!)}${NB}${r.unit}.`;
  });
  if (rs.length === 1) {
    lines.push(`x_max ${eq(L.xmax)}${NB}${r.unit} : ${rs[0]!.sp.text} ${used}.`);
  } else if (L.stoichiometric) {
    lines.push(
      `Toutes les valeurs sont égales : le mélange est stœchiométrique, ${usedAll} (x_max ${eq(L.xmax)}${NB}${r.unit}).`,
    );
  } else {
    const lim = L.limiting.map((i) => rs[i]!.sp.text);
    const exc = rs.filter((_, i) => !L.limiting.includes(i)).map((x) => x.sp.text);
    lines.push(
      `x_max est la plus petite valeur : x_max ${eq(L.xmax)}${NB}${r.unit}. ${lim.join(' et ')} ${lim.length > 1 ? 'sont les réactifs limitants' : 'est le réactif limitant'} ; ${exc.join(' et ')} ${exc.length > 1 ? 'sont' : 'est'} en excès.`,
    );
  }
  return lines;
}

/** n₀(B) qu'il faut pour un mélange stœchiométrique avec n₀(A) : n(A)/a = n(B)/b */
export const stoichPartner = (nA: number, a: number, b: number) => (nA * b) / a;

/* ---------- exercice « remplir l'état final » ---------- */

export interface Problem {
  r: Reaction;
  n0: number[];
}

type Rng = () => number;
const pick = <T>(xs: readonly T[], rng: Rng): T => xs[Math.floor(rng() * xs.length)]!;

/** réactions des exercices : transformations totales à deux réactifs suivis */
export const PRACTICE = REACTIONS.filter((r) => !r.tau && tracked(r.reactants).length === 2);

/** quantités « rondes » ; l'état final tombe juste à 2 décimales près */
const N0 = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6] as const;

/** au moins un coefficient différent de 1 parmi les réactifs suivis */
const withCoefficient = (r: Reaction) => tracked(r.reactants).some((x) => x.nu !== 1);

/**
 * Un problème : réaction et n₀. `stoich` : mélange stœchiométrique (et
 * coefficients différents, sinon c'est trivial) ; `rich` : au moins un
 * coefficient différent de 1 (défi : sinon x_max = le plus petit n₀).
 */
export function makeProblem(rng: Rng, stoich = false, rich = false): Problem {
  const pool = stoich
    ? PRACTICE.filter((r) => new Set(tracked(r.reactants).map((x) => x.nu)).size > 1)
    : rich
      ? PRACTICE.filter(withCoefficient)
      : PRACTICE;
  for (let k = 0; k < 500; k++) {
    const r = pick(pool, rng);
    const rs = tracked(r.reactants);
    const a = pick(N0, rng);
    let b: number = pick(N0, rng);
    if (stoich) b = stoichPartner(a, rs[0]!.nu, rs[1]!.nu);
    const n0 = [a, b];
    const L = limitOf(r, n0);
    if (![...amountsAt(r, n0, L.xmax), L.xmax, ...L.ratios, ...n0].every(exactShown)) continue;
    if (!stoich && L.stoichiometric) continue;
    if (b > 12) continue;
    return { r, n0 };
  }
  return { r: PRACTICE[0]!, n0: [2, 3] };
}

/** saisie française → nombre (« 1,5 », « 1.5 », « 0 ») ; null si illisible */
export function parseAmount(text: string): number | null {
  // « 1,5 mol », « ,5 », « 1, » : l'unité et la virgule seule sont tolérées
  const t2 = text
    .trim()
    .replace(/\s*m?mol\s*$/i, '')
    .replace(/\s/g, '')
    .replace(',', '.')
    .replace('−', '-');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(t2)) return null;
  return Number(t2);
}

/** juste à 1 % près (0 : exactement) */
export const near = (got: number, want: number) =>
  Math.abs(got - want) <= 0.01 * Math.abs(want) + 1e-9;

/* ---------- défi ---------- */

export type QuizKind = 'limiting' | 'xmax' | 'final' | 'excess' | 'stoich' | 'tau';

/**
 * Questions selon le niveau (rang : 2nde = 5, 1re = 6, Tle = 7) : en 2nde,
 * pas d'avancement (réactif limitant et proportions seulement) ; τ en Tle (et
 * niveau inconnu, comme l'Explorer).
 */
export function quizKinds(rank: number | null): QuizKind[] {
  if (rank !== null && rank <= 5) return ['limiting', 'stoich', 'limiting', 'stoich', 'limiting'];
  return rank === null || rank >= 7
    ? ['limiting', 'xmax', 'final', 'stoich', 'tau']
    : ['limiting', 'xmax', 'final', 'excess', 'stoich'];
}

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}

export interface Quiz {
  kind: QuizKind;
  question: string;
  /** équation affichée au-dessus des réponses */
  equation: string;
  equationSpoken: string;
  options: QuizOption[];
  explain: string;
}

function shuffle<T>(xs: readonly T[], rng: Rng): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * Options distinctes par valeur (la bonne gardée), quatre au plus ; s'il en
 * manque (coefficients égaux à 1…), `pad` complète avec des valeurs voisines
 * (× 2, ÷ 2…) et un indice général.
 */
function options(
  good: [string, number],
  wrongs: ReadonlyArray<readonly [string, number, string]>,
  rng: Rng,
  /** `max` : borne (exclue) des valeurs de remplissage — τ < 1 */
  pad?: { text: (v: number) => string; hint: string; max?: number },
): QuizOption[] {
  const out: QuizOption[] = [{ text: good[0], correct: true }];
  const seen = [good[1]];
  // une valeur arrondie se repérerait à l'œil : seules les valeurs exactes servent
  const add = (text: string, v: number, hint: string) => {
    if (out.length >= 4 || !(v > 0) || !exactShown(v) || seen.some((x) => close(x, v))) return;
    if (out.some((o) => o.text === text)) return;
    seen.push(v);
    out.push({ text, correct: false, hint });
  };
  for (const [text, v, hint] of wrongs) add(text, v, hint);
  if (pad)
    for (const k of [2, 0.5, 3, 1 / 3, 4]) {
      const v = good[1] * k;
      if (pad.max === undefined || v < pad.max) add(pad.text(v), v, pad.hint);
    }
  return shuffle(out, rng);
}

const initText = (p: Problem) =>
  tracked(p.r.reactants)
    .map((x, i) => `n₀(${x.sp.text}) = ${qty(p.n0[i]!, p.r.unit)}`)
    .join(' et ');

/**
 * Transformations réellement non totales pour τ : estérification (n₀
 * variables) et synthèse de l'ammoniac, écrites avec « ⇄ ».
 */
function tauProblem(rng: Rng): Problem {
  for (;;) {
    const r = reactionById(pick(['ester', 'ammonia'], rng));
    const n0 = [pick(N0, rng), pick(N0, rng)];
    const L = limitOf(r, n0);
    if ([L.xmax, ...L.ratios, ...n0].every(exactShown)) return { r, n0 };
  }
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  // « mélange stœchiométrique » est parfois la bonne réponse à « quel réactif limitant ? »
  const stoichLimiting = kind === 'limiting' && rng() < 0.2;
  const p =
    kind === 'tau' ? tauProblem(rng) : makeProblem(rng, kind === 'stoich' || stoichLimiting, true);
  const { r, n0 } = p;
  const rs = tracked(r.reactants);
  const ps = tracked(r.products);
  const L = limitOf(r, n0);
  const u = r.unit;
  const base = {
    kind,
    equation: kind === 'tau' ? equationText(r).replace('→', '⇄') : equationText(r),
    equationSpoken: equationSpoken(r),
  };
  const lim = rs[L.limiting[0]!]!;
  // mélange stœchiométrique : pas de réactif en excès (on prend l'autre pour les textes)
  const ex = rs.find((_, i) => !L.limiting.includes(i)) ?? rs[1]!;
  const exI = rs.indexOf(ex);
  // l'erreur classique : comparer les n₀ sans les diviser par les coefficients
  const naive = n0[0]! <= n0[1]! ? 0 : 1;
  switch (kind) {
    case 'limiting': {
      const naiveOk = naive !== L.limiting[0];
      const ratiosText = rs
        .map((x, i) => `${fmt(n0[i]!)}/${x.nu} ${eq(L.ratios[i]!)}`)
        .join(' et ');
      if (L.stoichiometric)
        return {
          ...base,
          question: `État initial : ${initText(p)}. Quel est le réactif limitant ?`,
          options: shuffle(
            [
              { text: 'Mélange stœchiométrique', correct: true },
              ...rs.map((x) => ({
                text: x.sp.text,
                correct: false,
                hint: `Les n₀ divisés par les coefficients sont égaux (${ratiosText}) : les réactifs s’épuisent ensemble.`,
              })),
            ],
            rng,
          ),
          explain: limitLines(r, n0).join(' '),
        };
      return {
        ...base,
        question: `État initial : ${initText(p)}. Quel est le réactif limitant ?`,
        options: shuffle(
          [
            { text: lim.sp.text, correct: true },
            {
              text: ex.sp.text,
              correct: false,
              hint: naiveOk
                ? `On ne compare pas les n₀ eux-mêmes, mais n₀ divisé par le coefficient : ${rs.map((x, i) => `${x.sp.text} : ${fmt(n0[i]!)}/${x.nu} ${eq(L.ratios[i]!)}`).join(' ; ')}.`
                : `${ex.sp.text} est en excès : il en reste à la fin.`,
            },
            {
              text: 'Mélange stœchiométrique',
              correct: false,
              hint: `Les n₀ divisés par les coefficients ne sont pas égaux : ${rs.map((x, i) => `${fmt(n0[i]!)}/${x.nu} ${eq(L.ratios[i]!)}`).join(' et ')}.`,
            },
          ],
          rng,
        ),
        explain: limitLines(r, n0).join(' '),
      };
    }
    case 'xmax': {
      const other = L.ratios[exI]!;
      return {
        ...base,
        question: `État initial : ${initText(p)}. Que vaut l’avancement maximal x_max ?`,
        options: options(
          [qty(L.xmax, u), L.xmax],
          [
            [
              qty(other, u),
              other,
              'C’est la plus GRANDE des valeurs n₀ ÷ coefficient : x_max est la plus petite (le réactif limitant s’épuise le premier).',
            ],
            [
              qty(n0[L.limiting[0]!]!, u),
              n0[L.limiting[0]!]!,
              `C’est n₀(${lim.sp.text}) : il faut le diviser par son coefficient ${lim.nu}.`,
            ],
            [
              qty(lim.nu * n0[L.limiting[0]!]!, u),
              lim.nu * n0[L.limiting[0]!]!,
              `On divise n₀ par le coefficient, on ne le multiplie pas : n₀ − ${lim.nu} x_max = 0.`,
            ],
            [
              qty(Math.min(...n0), u),
              Math.min(...n0),
              'C’est le plus petit n₀ : il faut d’abord diviser chaque n₀ par son coefficient.',
            ],
            [
              qty(n0[L.limiting[0]!]! / ex.nu, u),
              n0[L.limiting[0]!]! / ex.nu,
              `Chaque n₀ se divise par SON coefficient : ${lim.nu === 1 ? `x_max = n₀(${lim.sp.text})` : `x_max = n₀(${lim.sp.text}) / ${lim.nu}`}.`,
            ],
            [
              qty(n0[exI]!, u),
              n0[exI]!,
              `C’est n₀(${ex.sp.text}) ; or ${ex.sp.text} est en excès, x_max vient du réactif limitant.`,
            ],
          ],
          rng,
          {
            text: (v) => qty(v, u),
            hint: 'Divise chaque n₀ par son coefficient : x_max est la plus petite de ces valeurs.',
          },
        ),
        explain: limitLines(r, n0).join(' '),
      };
    }
    case 'final': {
      // une quantité de l'état final : un produit
      const all = [...rs, ...ps];
      const fin = amountsAt(r, n0, L.xmax);
      const j = rs.length + Math.floor(rng() * ps.length);
      const target = all[j]!;
      const v = fin[j]!;
      return {
        ...base,
        question: `État initial : ${initText(p)}, aucun produit. Quelle quantité de ${target.sp.text} obtient-on à l’état final ?`,
        options: options(
          [qty(v, u), v],
          [
            [
              qty(L.xmax, u),
              L.xmax,
              `n(${target.sp.text}) = ${target.nu} x_max : il faut multiplier par le coefficient ${target.nu}.`,
            ],
            [
              qty(target.nu * L.ratios[exI]!, u),
              target.nu * L.ratios[exI]!,
              `Tu as pris l’avancement du réactif en excès ; x_max ${eq(L.xmax)}${NB}${u}, fixé par le réactif limitant.`,
            ],
            [
              qty(target.nu * n0[L.limiting[0]!]!, u),
              target.nu * n0[L.limiting[0]!]!,
              `Il faut passer par x_max = n₀(${lim.sp.text}) / ${lim.nu}, pas multiplier n₀ directement.`,
            ],
            [
              qty(v / target.nu / target.nu, u),
              v / target.nu / target.nu,
              `n(${target.sp.text}) = ${target.nu} x_max : on multiplie par le coefficient, on ne divise pas.`,
            ],
            [
              qty(n0[exI]!, u),
              n0[exI]!,
              `C’est n₀(${ex.sp.text}), le réactif en excès ; la quantité de produit se calcule avec x_max.`,
            ],
          ],
          rng,
          {
            text: (v) => qty(v, u),
            hint: `Pour un produit, n = ν x_max, avec x_max ${eq(L.xmax)}${NB}${u}.`,
          },
        ),
        explain: `${limitLines(r, n0).at(-1)} Donc n(${target.sp.text}) = ${target.nu === 1 ? '' : `${target.nu} × `}x_max ${eq(v)}${NB}${u}.`,
      };
    }
    case 'excess': {
      const left = (n0[exI] ?? 0) - ex.nu * L.xmax;
      return {
        ...base,
        question: `État initial : ${initText(p)}. Quelle quantité du réactif en excès reste-t-il à l’état final ?`,
        options: options(
          [qty(left, u), left],
          [
            [
              qty((n0[exI] ?? 0) - L.xmax, u),
              (n0[exI] ?? 0) - L.xmax,
              `n(${ex.sp.text}) = n₀ − ${ex.nu} x_max : n’oublie pas le coefficient ${ex.nu}.`,
            ],
            [
              qty(ex.nu * L.xmax, u),
              ex.nu * L.xmax,
              `C’est la quantité CONSOMMÉE (${ex.nu} x_max) ; il reste n₀ − ${ex.nu} x_max.`,
            ],
            [
              qty(Math.abs((n0[exI] ?? 0) - (n0[L.limiting[0]!] ?? 0)), u),
              Math.abs((n0[exI] ?? 0) - (n0[L.limiting[0]!] ?? 0)),
              'On ne soustrait pas les quantités initiales : on passe par x_max et les coefficients.',
            ],
            [
              qty(n0[exI]!, u),
              n0[exI]!,
              `C’est la quantité initiale : une partie de ${ex.sp.text} a réagi (${ex.nu === 1 ? '' : `${ex.nu} `}x_max).`,
            ],
          ],
          rng,
          {
            text: (v) => qty(v, u),
            hint: `Il reste n₀ − ν x_max du réactif en excès, avec x_max ${eq(L.xmax)}${NB}${u}.`,
          },
        ),
        explain: `${limitLines(r, n0).at(-1)} Il reste n(${ex.sp.text}) = ${exprText(n0[exI]!, ex.nu, true).replace('x', 'x_max')} ${eq(left)}${NB}${u}.`,
      };
    }
    case 'stoich': {
      const [A, B] = rs as [Term, Term];
      const need = stoichPartner(n0[0]!, A.nu, B.nu);
      return {
        ...base,
        question: `On dispose de ${qty(n0[0]!, u)} de ${A.sp.text}. Quelle quantité de ${B.sp.text} faut-il pour un mélange stœchiométrique ?`,
        options: options(
          [qty(need, u), need],
          [
            [
              qty((n0[0]! * A.nu) / B.nu, u),
              (n0[0]! * A.nu) / B.nu,
              `Les coefficients sont inversés : n(${A.sp.text})/${A.nu} = n(${B.sp.text})/${B.nu}, donc n(${B.sp.text}) = ${B.nu} × n(${A.sp.text}) / ${A.nu}.`,
            ],
            [
              qty(n0[0]!, u),
              n0[0]!,
              'Autant de moles des deux réactifs ne suffit que si leurs coefficients sont égaux.',
            ],
            [
              qty(n0[0]! * B.nu * A.nu, u),
              n0[0]! * B.nu * A.nu,
              `On divise par le coefficient de ${A.sp.text}, on ne le multiplie pas.`,
            ],
            [
              qty(n0[0]! + B.nu, u),
              n0[0]! + B.nu,
              'Les coefficients multiplient les quantités, ils ne s’y ajoutent pas.',
            ],
          ],
          rng,
          {
            text: (v) => qty(v, u),
            hint: 'Proportions stœchiométriques : n(A)/a = n(B)/b.',
          },
        ),
        explain: `Mélange stœchiométrique : n(${A.sp.text})/${A.nu} = n(${B.sp.text})/${B.nu}, donc n(${B.sp.text}) = ${B.nu} × ${fmt(n0[0]!)} / ${A.nu} ${eq(need)}${NB}${u}.`,
      };
    }
    case 'tau': {
      const xmax = L.xmax;
      // x_f affiché exactement : τ se recalcule de x_f / x_max
      const tau =
        shuffle([0.1, 0.2, 0.25, 0.4, 0.5, 0.6, 0.75, 0.8], rng).find((v) =>
          exactShown(v * xmax),
        ) ?? 0.5;
      const xf = tau * xmax;
      return {
        ...base,
        question: `État initial : ${initText(p)}. La transformation n’est pas totale : à l’état final, on mesure x_f = ${qty(xf, u)}. Que vaut le taux d’avancement final τ ?`,
        options: options(
          [`τ = ${fmt(tau)}`, tau],
          [
            [
              `τ = ${fmt(xmax / xf)}`,
              xmax / xf,
              'τ = x_f / x_max, pas l’inverse : τ est compris entre 0 et 1.',
            ],
            ...(L.stoichiometric
              ? []
              : ([
                  [
                    `τ = ${fmt(xf / L.ratios[exI]!)}`,
                    xf / L.ratios[exI]!,
                    `x_max vient du réactif limitant (${lim.sp.text}) : x_max ${eq(xmax)}${NB}${u}.`,
                  ],
                ] as const)),
            [
              `τ = ${fmt(1 - tau)}`,
              1 - tau,
              'C’est la part de l’avancement qui MANQUE ; τ = x_f / x_max.',
            ],
          ],
          rng,
          { text: (v) => `τ = ${fmt(v)}`, hint: 'τ = x_f / x_max, compris entre 0 et 1.', max: 1 },
        ),
        explain: `x_max ${eq(xmax)}${NB}${u} (${L.stoichiometric ? 'mélange stœchiométrique' : `réactif limitant ${lim.sp.text}`}), donc τ = x_f / x_max = ${fmt(xf)} / ${fmt(xmax)} = ${fmt(tau)}, soit ${fmt(tau * 100)}${String.fromCharCode(0x202f)}%. τ < 1 : la transformation n’est pas totale.`,
      };
    }
  }
}
